import { useState } from 'react';
import {
  ActionIcon,
  Card,
  Collapse,
  Divider,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Modal,
  Button,
} from '@mantine/core';
import { ChevronDown, ChevronUp, ChevronsUp, ChevronsDown, Trash2 } from 'react-feather';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { SessionAgendaSection } from '../../../../../services/api/sessionAgenda';
import { RichTextEditor } from '../../../../../components/common/RichTextEditor/RichTextEditor';
import { SectionVisualsPicker } from './SectionVisualsPicker';
import { LeaderPromptsList } from './LeaderPromptsList';
import { SectionIncompleteBadge } from './IncompleteIndicator';

/**
 * MED-975 (E1.3d) — One agenda section card. Collapsed shows
 * "{section_name} ({duration} min)" + up/down + delete + chevron +
 * incomplete badge (when missing required fields). Expanded shows the
 * section's editable fields:
 *   - Section Name (TextInput) — AC-PS1
 *   - Duration (NumberInput) — AC-PS2; required for complete (AC-MC1)
 *   - SectionVisualsPicker — AC-PS3, AC-PS4
 *   - LeaderPromptsList — AC-PS5
 *
 * Delete confirmation (AC-AS5) fires only when the section has content
 * (any field non-empty); otherwise delete is a one-click action. This
 * matches the "less friction for empty rows" pattern used elsewhere in
 * Medley admin tooling.
 */

export interface AgendaSectionCardProps {
  section: SessionAgendaSection;
  position: number;
  total: number;
  /** True when this is the first section (disables up reorder). */
  isFirst: boolean;
  /** True when this is the last section (disables down reorder). */
  isLast: boolean;
  hasSlideDeck: boolean;
  slideDeckDoc: PDFDocumentProxy | null;
  /**
   * MED-980 (E1.3e / AC-SCF2, AC-SLC2). When false, the per-section
   * commitment toggles are hidden entirely (the AC explicitly forbids
   * showing them disabled — they must be absent from the form).
   */
  cohortCommitmentTrackingEnabled: boolean;
  onChange: (patch: Partial<SessionAgendaSection>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

function sectionHasContent(section: SessionAgendaSection): boolean {
  if (section.section_name && section.section_name.trim().length > 0) return true;
  if (section.duration_minutes !== null && section.duration_minutes !== undefined) return true;
  if (section.visuals.length > 0) return true;
  if (section.leader_prompts.some(p => p.intro_text || p.discussion_prompt)) return true;
  // MED-980 (E1.3e) — participant-facing fields also count as "has content"
  // so the delete confirmation fires when the CM has authored any of them.
  if (section.participant_prompt && section.participant_prompt.trim().length > 0) return true;
  if (section.show_commitment_field) return true;
  if (section.show_last_session_commitment) return true;
  return false;
}

export function AgendaSectionCard({
  section,
  position,
  total,
  isFirst,
  isLast,
  hasSlideDeck,
  slideDeckDoc,
  cohortCommitmentTrackingEnabled,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete,
}: AgendaSectionCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const nameMissing = !section.section_name || section.section_name.trim().length === 0;
  const durationMissing = section.duration_minutes === null || section.duration_minutes === undefined;
  // MED-1224 — a positively-entered 0 (or negative) duration is invalid and
  // must block save with a clear message. This is distinct from a blank/null
  // duration, which stays allowed as in-progress work (AC-MC3). Min valid = 1.
  const durationInvalid =
    section.duration_minutes !== null &&
    section.duration_minutes !== undefined &&
    section.duration_minutes < 1;
  const isIncomplete = nameMissing || durationMissing || durationInvalid;

  const handleDeleteClick = () => {
    if (sectionHasContent(section)) {
      setConfirmDelete(true);
    } else {
      onDelete();
    }
  };

  const headerLabel = section.section_name?.trim() || `Section ${position}`;
  const durationLabel =
    section.duration_minutes !== null && section.duration_minutes !== undefined
      ? `${section.duration_minutes} min`
      : 'No duration';

  return (
    <>
      {/* MED-1070: tinted surface (gray.0 = #F9F7F4) so each card reads as
          distinct against the white "Agenda Sections" panel — matches the
          Task Templates accordion surface. */}
      <Card withBorder padding="md" radius="md" bg="gray.0" data-testid="agenda-section-card">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Stack gap={2}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onMoveUp}
                disabled={isFirst}
                aria-label={`Move ${headerLabel} up`}
              >
                <ChevronsUp size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onMoveDown}
                disabled={isLast}
                aria-label={`Move ${headerLabel} down`}
              >
                <ChevronsDown size={16} />
              </ActionIcon>
            </Stack>
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="center" wrap="nowrap">
                <Text fw={600} truncate>
                  {headerLabel}
                </Text>
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                  ({durationLabel})
                </Text>
                <SectionIncompleteBadge isIncomplete={isIncomplete} />
              </Group>
              <Text size="xs" c="dimmed">
                Section {position} of {total}
              </Text>
            </Stack>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color="dangerRed"
              onClick={handleDeleteClick}
              aria-label={`Delete ${headerLabel}`}
              data-testid="agenda-section-delete"
            >
              <Trash2 size={18} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setExpanded(v => !v)}
              aria-label={expanded ? 'Collapse section' : 'Expand section'}
              aria-expanded={expanded}
              data-testid="agenda-section-toggle"
            >
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </ActionIcon>
          </Group>
        </Group>

        <Collapse in={expanded}>
          <Divider my="md" />
          <Stack gap="md">
            <Group grow align="flex-start">
              <TextInput
                label="Section Name"
                placeholder="e.g., Welcome & Check in"
                required
                value={section.section_name ?? ''}
                error={nameMissing ? 'Required' : undefined}
                onChange={e => onChange({ section_name: e.currentTarget.value || null })}
                data-testid="agenda-section-name"
              />
              <NumberInput
                label="Duration (minutes)"
                placeholder=""
                required
                // MED-1224 — minimum valid duration is 1 minute; 0 and negatives
                // are rejected. allowNegative={false} stops a minus sign at the
                // keystroke level; clampBehavior="none" lets a typed 0 persist so
                // the validation message below actually shows instead of Mantine
                // silently clamping 0 -> 1 on blur (which would hide the error).
                min={1}
                allowNegative={false}
                clampBehavior="none"
                value={section.duration_minutes ?? ''}
                error={
                  durationMissing
                    ? 'Required'
                    : durationInvalid
                      ? 'Duration must be at least 1 minute'
                      : undefined
                }
                // Mantine v8 NumberInput onChange returns string | number — see
                // CLAUDE.md memory note. Coerce with parseFloat to avoid the
                // typeof==='number' guard that silently drops edits. Math.floor
                // truncates non-integers to a whole number (AC4).
                onChange={value => {
                  if (value === '' || value === null || value === undefined) {
                    onChange({ duration_minutes: null });
                    return;
                  }
                  const n = typeof value === 'number' ? value : parseFloat(String(value));
                  onChange({ duration_minutes: Number.isFinite(n) ? Math.floor(n) : null });
                }}
                data-testid="agenda-section-duration"
              />
            </Group>

            {/*
              MED-980 (E1.3e / AC-PP1, AC-PP2, AC-PP3) — Participant Prompt.
              Lives directly under the name/duration row (above Section
              Visuals) to mirror the beta field order. Label and helper text
              are explicit about audience to distinguish from the
              facilitator-only Leader Prompts below.
            */}
            <Stack gap={4}>
              <RichTextEditor
                label="Participant Prompt"
                placeholder="Instructions or context shown to participants during this section..."
                value={section.participant_prompt ?? ''}
                onChange={html =>
                  onChange({ participant_prompt: html && html.length > 0 ? html : null })
                }
                minHeight={120}
              />
              <Text size="xs" c="dimmed">
                Shown to participants in-session. Distinct from the facilitator-only Leader Prompts
                below.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text fw={600} size="sm">
                Section Visuals
              </Text>
              <SectionVisualsPicker
                hasSlideDeck={hasSlideDeck}
                slideDeckDoc={slideDeckDoc}
                visuals={section.visuals}
                onChange={visuals => onChange({ visuals })}
              />
            </Stack>

            {/*
              MED-980 (E1.3e / AC-SCF, AC-SLC) — Commitment toggles. Rendered
              ONLY when the cohort has commitment_tracking_enabled = true.
              When the cohort setting is off, the entire block is absent
              (not greyed out — see AC-SCF2 / AC-SLC2). The two switches sit
              between Section Visuals and Leader Prompts per the beta
              layout (matches Image #7's "Closing" section example).
            */}
            {cohortCommitmentTrackingEnabled && (
              <Stack gap="sm" data-testid="agenda-section-commitment-toggles">
                <Switch
                  label="Show Commitment Field"
                  description="Participants can enter a commitment during this section."
                  checked={section.show_commitment_field}
                  onChange={e => onChange({ show_commitment_field: e.currentTarget.checked })}
                  color="medleyGreen"
                  data-testid="agenda-section-show-commitment-field"
                />
                <Switch
                  label="Show Last Session's Commitment"
                  description="Display the participant's commitment from their previous session."
                  checked={section.show_last_session_commitment}
                  onChange={e =>
                    onChange({ show_last_session_commitment: e.currentTarget.checked })
                  }
                  color="medleyGreen"
                  data-testid="agenda-section-show-last-session-commitment"
                />
              </Stack>
            )}

            <Divider />

            <LeaderPromptsList
              prompts={section.leader_prompts}
              onChange={leader_prompts => onChange({ leader_prompts })}
            />
          </Stack>
        </Collapse>
      </Card>

      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this section?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            <Text component="span" fw={600}>
              {headerLabel}
            </Text>{' '}
            has content. Deleting it will discard the name, duration, slide associations, and any
            leader prompts in this section.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              color="dangerRed"
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
            >
              Delete Section
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
